import { ReactNode, useEffect, useMemo, useState } from 'react'

import styles from './DomainListTable.module.scss'

import Input from '@/components/Input'
import Pagination from '@/components/Pagination'

const PAGE_SIZE = 25

export interface DomainRow {
  domain: string
  // classKey must be a pill class defined in this component's styles.module.scss
  pill?: { label: string; classKey: string }
}

interface DomainListTableProps {
  rows: DomainRow[]
  isLoading: boolean
  hasError: boolean
  listName: string
  banner: ReactNode
  totalChip: string
  controls?: ReactNode
  pillHeader?: string
  resetPageKey?: unknown
}

const DomainListTable = ({
  rows,
  isLoading,
  hasError,
  listName,
  banner,
  totalChip,
  controls,
  pillHeader,
  resetPageKey,
}: DomainListTableProps) => {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)

  useEffect(() => {
    setPage(0)
  }, [resetPageKey])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    return query ? rows.filter((row) => row.domain.includes(query)) : rows
  }, [rows, search])

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const clampedPage = Math.min(page, pageCount - 1)
  const pageRows = filtered.slice(clampedPage * PAGE_SIZE, (clampedPage + 1) * PAGE_SIZE)

  if (isLoading) {
    return <div className={styles.loadingState}>Loading {listName}...</div>
  }

  if (hasError) {
    return <div className={styles.errorBanner}>Failed to load {listName}</div>
  }

  return (
    <div className={styles.content}>
      <div className={styles.infoBanner}>{banner}</div>

      <div className={styles.controls}>
        <Input
          type="text"
          value={search}
          onInput={(text) => {
            setSearch(text)
            setPage(0)
          }}
          placeholder="Search domains..."
          className={styles.searchInput}
        />
        {controls}
        <span className={styles.totalChip}>{totalChip}</span>
      </div>

      <div className={styles.tableContainer}>
        {pageRows.length === 0 ? (
          <div className={styles.emptyState}>No domains match the current filters.</div>
        ) : (
          <table className={styles.table} aria-label={listName}>
            <thead className={styles.tableHeader}>
              <tr>
                <th scope="col">Domain</th>
                {pillHeader && <th scope="col">{pillHeader}</th>}
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {pageRows.map((row) => (
                <tr key={row.domain}>
                  <td>
                    <span className={styles.domainName}>{row.domain}</span>
                  </td>
                  {pillHeader && (
                    <td>
                      {row.pill && (
                        <span className={`${styles.pill} ${styles[row.pill.classKey]}`}>{row.pill.label}</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination
        page={clampedPage}
        pageSize={PAGE_SIZE}
        total={filtered.length}
        onPrev={() => setPage(Math.max(0, clampedPage - 1))}
        onNext={() => setPage(Math.min(pageCount - 1, clampedPage + 1))}
      />
    </div>
  )
}

export default DomainListTable
